/**
 * ImageCropDialog · iter155 (2026-02-25) · generisch
 *
 * Modal mit react-easy-crop. Aspect via Prop wählbar (z. B. 3/1 für Coverbild, 1
 * für Avatar). `restrictPosition={true}` sorgt dafür, dass der Ausschnitt
 * IMMER innerhalb der Bildgrenzen bleibt.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import { X, Check, Loader2 } from "lucide-react";

export default function ImageCropDialog({ file, src, aspect = 1, title, onCropped, onCancel, outputType = "image/jpeg", returnCoords = false }) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(file);
    } else if (src) {
      setImageSrc(src);
    }
  }, [file, src]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const finalize = async () => {
    if (!imageSrc || !croppedPixels) return;
    // iter278 · Koordinaten-Modus: nur die Pixel-Box zurückgeben (serverseitiger Zuschnitt).
    if (returnCoords) {
      onCropped(croppedPixels);
      return;
    }
    setBusy(true);
    try {
      const blob = await cropToBlob(imageSrc, croppedPixels, file?.name || "image.jpg", outputType);
      onCropped(blob);
    } catch (e) {
      console.error("crop failed", e);
    } finally {
      setBusy(false);
    }
  };

  const cropAreaHeight = aspect >= 3 ? 220 : 380;

  return (
    <div
      data-testid="cover-crop-dialog"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/80 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="w-full max-w-2xl bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-4 h-12 border-b border-[#E5E5E5]">
          <p className="text-sm font-bold">{title || "Ausschnitt wählen"}</p>
          <button
            data-testid="cover-crop-close"
            onClick={onCancel}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]"
            aria-label={t("common.close_aria")}
          >
            <X size={16} />
          </button>
        </div>
        <div className="relative bg-[#111] w-full" style={{ height: cropAreaHeight }}>
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              showGrid
              restrictPosition={true}
              cropShape={aspect === 1 ? "round" : "rect"}
            />
          )}
        </div>
        <div className="px-5 py-3 flex items-center gap-3 text-xs text-[#666]">
          <span className="uppercase font-bold tracking-wider">Zoom</span>
          <input
            data-testid="cover-crop-zoom"
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 accent-[#0A0A0A]"
          />
          <span className="w-10 text-right tabular-nums">{zoom.toFixed(2)}×</span>
        </div>
        <div className="px-5 pb-2 pt-1 text-[11px] text-[#999]">
          Ziehe das Bild an die gewünschte Position und passe den Zoom an.
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-[#E5E5E5]">
          <button
            data-testid="cover-crop-cancel"
            onClick={onCancel}
            className="h-10 px-4 rounded-full border border-[#E5E5E5] text-xs font-semibold text-[#666] hover:bg-[#F5F5F5]"
          >
            Abbrechen
          </button>
          <button
            data-testid="cover-crop-confirm"
            onClick={finalize}
            disabled={busy || !croppedPixels}
            className="h-10 px-5 rounded-full bg-[#0A0A0A] text-white text-xs font-bold hover:bg-[#222] disabled:opacity-40 flex items-center gap-2"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Ausschnitt übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}

async function cropToBlob(src, pixelArea, filename, outputType = "image/jpeg") {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = pixelArea.width;
  canvas.height = pixelArea.height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    img,
    pixelArea.x, pixelArea.y, pixelArea.width, pixelArea.height,
    0, 0, pixelArea.width, pixelArea.height,
  );
  const isPng = outputType === "image/png";
  const ext = isPng ? "png" : "jpg";
  const baseName = (filename || "image").replace(/\.[^.]+$/, "");
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("Canvas toBlob failed"));
        const out = new File([blob], `${baseName}.${ext}`, { type: outputType });
        resolve(out);
      },
      outputType,
      isPng ? undefined : 0.92,
    );
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
